import { useState, useEffect, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Navigate } from 'react-router-dom';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import Select from 'react-select';
import { FaEdit, FaTrash, FaCheck } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';

function Consultations() {
  const { isAdmin } = useContext(AuthContext);
  const { data: consultations, loading, error, fetchData } = useApi('/consultations');
  const { data: specialists } = useApi('/users/userRole/specialist');
  const { data: farmers } = useApi('/users/userRole/farmer');
  const { data: generalUsers } = useApi('/users/userRole/user');

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    specialistId: '',
    symptoms: '',
    consultationType: '',
    bookingDateTime: '',
  });

  // Combine farmers and general users into a single array
  const combinedUsers = useMemo(() => {
    const usersSet = new Set();
    const allUsers = [
      ...(farmers || []).map(user => ({ ...user, role: 'farmer' })),
      ...(generalUsers || []).map(user => ({ ...user, role: 'user' })),
    ];
    // Remove duplicates based on _id
    return allUsers.filter(user => {
      if (usersSet.has(user._id)) return false;
      usersSet.add(user._id);
      return true;
    });
  }, [farmers, generalUsers]);

  useEffect(() => {
    console.log('Consultations data:', consultations); // Debug
  }, [consultations]);

  if (!isAdmin()) {
    toast.error('Access denied. Admins only.');
    return <Navigate to="/login" />;
  }

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name) => (selected) => {
    setFormData({ ...formData, [name]: selected ? selected.value : '' });
    if (name === 'specialistId') {
      setSelectedSpecialist(specialists?.find((s) => s._id === selected?.value) || null);
    }
  };

  const handleDateTimeChange = (e) => {
    setFormData({ ...formData, bookingDateTime: new Date(e.target.value).toISOString() });
  };

  const handleBookConsultation = async (e) => {
    e.preventDefault();
    try {
      await apiService.post('/consultations/admin', {
        ...formData,
        symptoms: formData.symptoms.split(',').map((s) => s.trim()),
      });
      toast.success('Consultation booked successfully');
      setIsBookingModalOpen(false);
      setFormData({
        userId: '',
        specialistId: '',
        symptoms: '',
        consultationType: '',
        bookingDateTime: '',
      });
      setSelectedSpecialist(null);
      await fetchData();
    } catch (error) {
      console.error('Book consultation error:', error);
      // apiService handles toast
    }
  };

  const handleEditConsultation = async (e) => {
    e.preventDefault();
    try {
      await apiService.put(`/consultations/${selectedConsultation._id}`, {
        status: formData.status,
        specialistDiagnosis: formData.specialistDiagnosis,
        bookingDateTime: formData.bookingDateTime,
      });
      toast.success('Consultation updated successfully');
      setIsEditModalOpen(false);
      setSelectedConsultation(null);
      setFormData({
        userId: '',
        specialistId: '',
        symptoms: '',
        consultationType: '',
        bookingDateTime: '',
      });
      await fetchData();
    } catch (error) {
      console.error('Edit consultation error:', error);
      // apiService handles toast
    }
  };

  const handleDeleteConsultation = async (id) => {
    if (window.confirm('Are you sure you want to delete this consultation?')) {
      try {
        await apiService.delete(`/consultations/admin/${id}`);
        toast.success('Consultation deleted successfully');
        await fetchData();
      } catch (error) {
        console.error('Delete consultation error:', error);
        // apiService handles toast
      }
    }
  };

  const handleAcceptConsultation = async (id) => {
    try {
      await apiService.patch(`/consultations/accept/${id}`);
      toast.success('Consultation accepted successfully');
      await fetchData();
    } catch (error) {
      console.error('Accept consultation error:', error);
      // apiService handles toast
    }
  };

  const openEditModal = (consultation) => {
    setSelectedConsultation(consultation);
    setFormData({
      userId: consultation.userId?._id || '',
      specialistId: consultation.specialistId?._id || '',
      symptoms: consultation.symptoms?.join(', ') || '',
      consultationType: consultation.consultationType || '',
      status: consultation.status || 'pending',
      specialistDiagnosis: consultation.specialistDiagnosis?.notes || '',
      bookingDateTime: consultation.bookingDateTime || '',
    });
    setIsEditModalOpen(true);
  };

  const specialistOptions = (specialists || []).map((s) => ({
    value: s._id || '',
    label: `${s.name} (${s.specialty || 'N/A'})`,
  }));

  const userOptions = (combinedUsers || []).map((u) => ({
    value: u._id || '',
    label: `${u.name || 'N/A'} (${u.role || 'N/A'})`,
  }));

  const columns = [
    {
      key: 'farmer',
      label: 'User/Farmer',
      render: (item) => item.userId?.name || 'N/A',
    },
    {
      key: 'specialist',
      label: 'Specialist',
      render: (item) => item.specialistId?.name || 'N/A',
    },
    {
      key: 'symptoms',
      label: 'Symptoms',
      render: (item) => item.symptoms?.join(', ') || 'N/A',
    },
    { key: 'consultationType', label: 'Type' },
    {
      key: 'bookingDateTime',
      label: 'Booking Date & Time',
      render: (item) => item.bookingDateTime ? new Date(item.bookingDateTime).toLocaleString() : 'N/A',
    },
    { key: 'status', label: 'Status' },
    {
      key: 'disease',
      label: 'Disease',
      render: (item) => item.diseaseId?.name || item.specialistDiagnosis?.diseaseId?.name || 'N/A',
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <div className="flex space-x-2">
          <button
            onClick={() => openEditModal(item)}
            className="text-blue-500 hover:text-blue-700"
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => handleDeleteConsultation(item._id)}
            className="text-red-500 hover:text-red-700"
            title="Delete"
          >
            <FaTrash />
          </button>
          {item.status === 'pending' && (
            <button
              onClick={() => handleAcceptConsultation(item._id)}
              className="text-green-500 hover:text-green-700"
              title="Accept"
            >
              <FaCheck />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-5 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Consultation Management</h2>
        <button
          onClick={() => setIsBookingModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 shadow-md"
        >
          Book Consultation
        </button>
      </div>
      {loading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          Loading...
        </div>
      ) : error ? (
        <div className="card text-center text-red-600 dark:text-red-400 p-5 rounded-lg shadow-md">
          Error: {error}
        </div>
      ) : !Array.isArray(consultations) || consultations.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          No consultations found.
        </div>
      ) : (
        <div className="card bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
          <Table columns={columns} data={consultations} />
        </div>
      )}

      <Modal
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
          setSelectedSpecialist(null);
          setFormData({
            userId: '',
            specialistId: '',
            symptoms: '',
            consultationType: '',
            bookingDateTime: '',
          });
        }}
        title="Book Consultation"
        className="max-w-full lg:max-w-4xl mx-4"
      >
        <div className="p-5">
          <form onSubmit={handleBookConsultation}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select User/Farmer
                </label>
                <Select
                  options={userOptions}
                  value={userOptions.find((opt) => opt.value === formData.userId) || null}
                  onChange={handleSelectChange('userId')}
                  placeholder="Select a user or farmer"
                  isClearable
                  className="mb-4"
                  classNamePrefix="react-select"
                />
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Specialist
                </label>
                <Select
                  options={specialistOptions}
                  value={specialistOptions.find((opt) => opt.value === formData.specialistId) || null}
                  onChange={handleSelectChange('specialistId')}
                  placeholder="Select a specialist"
                  isClearable
                  className="mb-4"
                  classNamePrefix="react-select"
                />
                <FormInput
                  label="Symptoms (comma-separated)"
                  name="symptoms"
                  type="text"
                  value={formData.symptoms}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                />
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Consultation Type
                  </label>
                  <select
                    name="consultationType"
                    value={formData.consultationType}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="remote">Remote</option>
                    <option value="on-site">On-Site</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Booking Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    name="bookingDateTime"
                    value={formData.bookingDateTime ? formData.bookingDateTime.slice(0, 16) : ''}
                    onChange={handleDateTimeChange}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-700"
                    required
                  />
                </div>
              </div>
              <div>
                {selectedSpecialist ? (
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                      Specialist Profile
                    </h3>
                    <img
                      src={selectedSpecialist.profilePicture || 'https://via.placeholder.com/150'}
                      alt={selectedSpecialist.name}
                      className="w-32 h-32 rounded-full mb-4"
                    />
                    <p className="text-gray-700 dark:text-gray-300">
                      <strong>Name:</strong> {selectedSpecialist.name}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      <strong>Specialty:</strong> {selectedSpecialist.specialty || 'N/A'}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      <strong>Experience:</strong> {selectedSpecialist.experience || 'N/A'}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      <strong>Rating:</strong> {selectedSpecialist.rating ? `${selectedSpecialist.rating}/5` : 'N/A'}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">
                    Select a specialist to view their profile.
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsBookingModalOpen(false);
                  setSelectedSpecialist(null);
                  setFormData({
                    userId: '',
                    specialistId: '',
                    symptoms: '',
                    consultationType: '',
                    bookingDateTime: '',
                  });
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Book Consultation
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedConsultation(null);
          setFormData({
            userId: '',
            specialistId: '',
            symptoms: '',
            consultationType: '',
            bookingDateTime: '',
          });
        }}
        title="Edit Consultation"
        className="max-w-full lg:max-w-md mx-auto"
      >
        <div className="p-5">
          <form onSubmit={handleEditConsultation}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="completed">Completed</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>
            <FormInput
              label="Diagnosis"
              name="specialistDiagnosis"
              type="textarea"
              value={formData.specialistDiagnosis || ''}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
            />
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Booking Date & Time
              </label>
              <input
                type="datetime-local"
                name="bookingDateTime"
                value={formData.bookingDateTime ? formData.bookingDateTime.slice(0, 16) : ''}
                onChange={handleDateTimeChange}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-700"
                required
              />
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setSelectedConsultation(null);
                  setFormData({
                    userId: '',
                    specialistId: '',
                    symptoms: '',
                    consultationType: '',
                    bookingDateTime: '',
                  });
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}

export default Consultations;